import {GridBox} from '@esgi/ui/layout';
import {TestRow} from '../row';
import {OverlayScrollbarsComponent} from 'overlayscrollbars-react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Test, TestType} from '../../../../service/types';
import {OverlayScrollbarsComponentRef} from 'overlayscrollbars-react/types/OverlayScrollbarsComponent';
import {Skeleton} from '../skeleton';
import {lazyComponent} from '@esgi/core/react';
import {isNull} from 'underscore';
import {TestDetailsState} from './types';
import {useUser} from '@esgi/core/authentication';

const TestDetails = lazyComponent(() => import('shared/modules/test-details/test-details'));
const RubricDetails = lazyComponent(() => import('modules/assets/tests/rubric/details/root'));

type Props = {
	tests: Test[];
	selectedTests: number[];
	loaded: boolean;
	onTestSelect: (id: number) => void;
	fetchData: VoidFunction
	osRef: React.MutableRefObject<OverlayScrollbarsComponentRef<'div'>>;
	hasMoreTests: boolean;
};

export function Body({tests, selectedTests, loaded, onTestSelect, fetchData, osRef, hasMoreTests}: Props) {
	const user = useUser();

	const [testDetailsState, setTestDetailsState] = useState<TestDetailsState | null>(null);

	const observerTarget = useRef();

	useEffect(() => {
		if (!observerTarget.current) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasMoreTests) {
					fetchData();
				}
			},
			{threshold: 0},
		);

		const target = observerTarget.current;

		if (target) {
			observer.observe(target);
		}
		return () => {
			if (target) {
				observer.unobserve(target);
			}
		};
	}, [hasMoreTests]);

	const closeTestDetails = useCallback(() => {
		setTestDetailsState(null);
	}, []);

	return (
		<>
			<OverlayScrollbarsComponent ref={osRef} defer options={{scrollbars: {autoHide: 'leave'}}}>
				<GridBox gap={2}>
					{tests.map((test) => (
						<TestRow
							key={test.id + test.created}
							testInfo={test}
							onTestSelect={onTestSelect}
							selected={selectedTests?.includes(test.id) || test.disabled}
							onTestNameClick={setTestDetailsState}
						/>
					))}
					{!loaded && <Skeleton />}
				</GridBox>
				<div ref={observerTarget} />
			</OverlayScrollbarsComponent>

			{!isNull(testDetailsState) &&
				(testDetailsState.testType === TestType.Score || testDetailsState.testType === TestType.YN) && (
					<TestDetails
						testID={testDetailsState.testId}
						firstName={user.firstName}
						lastName={user.lastName}
						userID={user.userID}
						close={closeTestDetails}
						disableCopyTest
					/>
			)}

			{!isNull(testDetailsState) && testDetailsState.testType === TestType.Rubric && (
				<RubricDetails testID={testDetailsState.testId} disableCopy disableAddToSubject close={closeTestDetails} />
			)}
		</>
	);
}
